import {
  Component,
  Input,
  Output,
  EventEmitter,
  ViewChild,
  OnInit,
  OnChanges,
  SimpleChanges,
  ElementRef,
  ViewEncapsulation,
} from '@angular/core';
import { CreateStringValueAccessorProvider, CreateStringValueValidationProvider } from '../value-accessors/string-value-accessor';
import { ImageLookupComponent } from '../../../config/image-lookup/image-lookup.component';
import { HtmlTemplateService } from '../../../templates/shared/html-template.service';
import { HtmlTemplate } from '../../../templates/shared/html-template.model';
import { CkEditorPoolService } from '../ckeditor-pool.service';
import { PooledCkEditorHost } from '../pooled-ckeditor-host';
/**
 * A component that allows you to edit text in the wysiwyg manner and outputs HTML.
 *
 * Encapselates the CKEditor as of Jan 2017.
 * Not licensed.
 *
 * https://github.com/chymz/ng2-ckeditor
 * http://docs.cksource.com/Main_Page
 *
 *
 * Just be glad I didn't name it "wussywyg", which is normally how I pronounce it - Hiroki
 */
@Component({
  selector: 'wl-wysiwyg-editor',
  templateUrl: 'wysiwyg-editor.component.html',
  styleUrls: ['ckeditor.less'],
  standalone: false,
  providers: [CreateStringValueAccessorProvider(WlWysiwygEditorComponent), CreateStringValueValidationProvider(WlWysiwygEditorComponent)],
  encapsulation: ViewEncapsulation.None,
})
export class WlWysiwygEditorComponent extends PooledCkEditorHost implements OnInit, OnChanges {
  @ViewChild('editorHost', { static: false }) _editorHost: ElementRef<HTMLElement>;
  @ViewChild('imageLookup', { static: false }) _imageLookup: ImageLookupComponent;

  @Input() public required: any;

  /**
   * how often does ckeditor update the model
   */
  @Input() public debounce: number = 500;
  @Input() public editorName = 'ckEditor';

  /**
   * start editor in advanced mode
   */
  @Input() public advanced: boolean = false;

  @Input() public isReadOnly: boolean = false;

  /**
   * Whether to block pasted or dropped images as base64. Default is true.
   */
  @Input() public compact: boolean = false;
  @Input() public blockEmbeddedBase64Images: boolean = true;

  // repeat events from the ckeditor component
  @Output() public change: EventEmitter<string> = new EventEmitter<string>();
  // not sure what object ckeditor is emitting for the ready event
  @Output() public ready: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public blur: EventEmitter<Event> = new EventEmitter<Event>();
  @Output() public focus: EventEmitter<Event> = new EventEmitter<Event>();

  public _enabled: boolean = true;

  public _htmlTemplateService: HtmlTemplateService;

  public showImageSelector: boolean;
  @Input() public disableImageSelector: boolean;
  @Input() public showHtmlTemplates: boolean;
  public htmlTemplates: Array<HtmlTemplate>;
  public selectedHtmlTemplate: string;

  /**
   * constructor
   */

  public constructor(htmlTemplateService: HtmlTemplateService, ckEditorPool: CkEditorPoolService) {
    super(ckEditorPool);
    this._htmlTemplateService = htmlTemplateService;

    this.showImageSelector = true;
    this.showHtmlTemplates = true;
    this.disableImageSelector = false;
    this.htmlTemplates = new Array<HtmlTemplate>();
    this.selectedHtmlTemplate = '';
  }

  protected get editorHostElement() {
    return this._editorHost;
  }

  protected get editorReadOnly() {
    return !!this.isReadOnly;
  }

  ngOnInit() {
    if (this.showHtmlTemplates == true) {
      this._htmlTemplateService.getHtmlTemplates().then((data: Array<HtmlTemplate>) => {
        this.htmlTemplates = data;
        this.htmlTemplates.splice(0, 0, new HtmlTemplate());
      });
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isReadOnly']) {
      this._updateReadOnly();
    }
  }

  public toggleShowImageSelector() {
    this.showImageSelector = !this.showImageSelector;
  }

  public insertSelectedImage() {
    if (this._imageLookup && this._imageLookup.selectedImage) {
      this._insertHtmlAtSavedSelection('<img src="' + this._imageLookup.selectedImage.AbsolutePath + '">');
    }
  }

  public imageChanged(event: any) {
    this.insertSelectedImage();
  }

  /**
   * override super class's isValid
   */
  public isValid = (): boolean => {
    return !this.required || (this._value != null && this._value != '');
  };

  public handleHtmlTemplateChange(data: string) {
    this._insertHtmlAtSavedSelection(data);
  }
}
